import { Edit as EditIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { adminOrderItemService } from "../../../services/adminOrderItemService";
import { BulkUpdateItemFieldsRequest } from "../../../types/admin";

interface BulkEditItemFieldsModalProps {
  open: boolean;
  onClose: () => void;
  selectedItemIds: string[];
  onSuccess: () => void;
}

const BulkEditItemFieldsModal: React.FC<BulkEditItemFieldsModalProps> = ({
  open,
  onClose,
  selectedItemIds,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [retailerOrderId, setRetailerOrderId] = useState("");
  const [retailerTrackingId, setRetailerTrackingId] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [estimatedDelivery, setEstimatedDelivery] = useState("");
  const [retailer, setRetailer] = useState("");
  const [cargoWeight, setCargoWeight] = useState("");
  const [itemCarrierCargoFee, setItemCarrierCargoFee] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [notes, setNotes] = useState("");

  const resetForm = () => {
    setRetailerOrderId("");
    setRetailerTrackingId("");
    setTrackingNumber("");
    setEstimatedDelivery("");
    setRetailer("");
    setCargoWeight("");
    setItemCarrierCargoFee("");
    setFlightNumber("");
    setNotes("");
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    // Build request with only non-empty fields
    const request: BulkUpdateItemFieldsRequest = {
      orderItemIds: selectedItemIds,
    };

    if (retailerOrderId.trim()) request.retailerOrderId = retailerOrderId.trim();
    if (retailerTrackingId.trim()) request.retailerTrackingId = retailerTrackingId.trim();
    if (trackingNumber.trim()) request.trackingNumber = trackingNumber.trim();
    if (estimatedDelivery) request.estimatedDelivery = estimatedDelivery;
    if (retailer.trim()) request.retailer = retailer.trim();
    if (cargoWeight !== "" && cargoWeight !== undefined) request.cargoWeight = parseFloat(cargoWeight);
    if (itemCarrierCargoFee !== "" && itemCarrierCargoFee !== undefined) request.itemCarrierCargoFee = parseFloat(itemCarrierCargoFee);
    if (flightNumber.trim()) request.flightNumber = flightNumber.trim();
    if (notes.trim()) request.notes = notes.trim();

    // Check if at least one field is filled
    const hasAnyField =
      request.retailerOrderId ||
      request.retailerTrackingId ||
      request.flightNumber ||
      request.trackingNumber ||
      request.estimatedDelivery ||
      request.retailer ||
      request.cargoWeight !== undefined ||
      request.itemCarrierCargoFee !== undefined ||
      request.notes;

    if (!hasAnyField) {
      setError(t("admin.orders.bulkEditNoFieldsFilled"));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await adminOrderItemService.bulkUpdateItemFields(request);
      resetForm();
      onSuccess();
      onClose();
    } catch {
      setError(t("admin.orders.bulkEditFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EditIcon />
          {t("admin.orders.bulkEditItems", { count: selectedItemIds.length })}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
          {t("admin.orders.bulkEditHint")}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.retailer")}
              value={retailer}
              onChange={(e) => setRetailer(e.target.value)}
              disabled={loading}
              placeholder={t("admin.retailerPlaceholder")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.trackingNumber")}
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              disabled={loading}
              placeholder={t("admin.trackingNumberPlaceholder")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.retailerOrderId")}
              value={retailerOrderId}
              onChange={(e) => setRetailerOrderId(e.target.value)}
              disabled={loading}
              placeholder={t("admin.retailerOrderIdPlaceholder")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.retailerTrackingId")}
              value={retailerTrackingId}
              onChange={(e) => setRetailerTrackingId(e.target.value)}
              disabled={loading}
              placeholder={t("admin.retailerTrackingIdPlaceholder")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.orders.flightNumber")}
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value)}
              disabled={loading}
              placeholder={t("admin.orders.flightNumberPlaceholder")}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.estimatedDelivery")}
              type="date"
              value={estimatedDelivery}
              onChange={(e) => setEstimatedDelivery(e.target.value)}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.cargoWeight")}
              type="number"
              value={cargoWeight}
              onChange={(e) => setCargoWeight(e.target.value)}
              disabled={loading}
              placeholder={t("admin.cargoWeightPlaceholder")}
              inputProps={{ step: "0.01", min: "0" }}
              helperText={t("admin.cargoWeightHelperText")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.itemCarrierCargoFee")}
              type="number"
              value={itemCarrierCargoFee}
              onChange={(e) => setItemCarrierCargoFee(e.target.value)}
              disabled={loading}
              placeholder={t("admin.itemCarrierCargoFeePlaceholder")}
              inputProps={{ step: "0.01", min: "0" }}
              helperText={t("admin.itemCarrierCargoFeeHelperText")}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("common.notes")}
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              placeholder={t("admin.itemNotesPlaceholder")}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? t("admin.saving") : t("admin.saveChanges")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditItemFieldsModal;
